function isYouTubePlayerReady() {
  return !!(window.YT && typeof window.YT.Player === 'function');
}

function flushYouTubeReadyQueue() {
  const queue = window.__idtYouTubeReadyQueue || [];
  window.__idtYouTubeReadyQueue = [];

  queue.forEach((callback) => {
    try {
      callback();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('YouTube callback error', error);
    }
  });
}

function waitForYouTubePlayer(onReady) {
  if (isYouTubePlayerReady()) {
    onReady();
    return;
  }

  window.__idtYouTubeReadyQueue = window.__idtYouTubeReadyQueue || [];
  window.__idtYouTubeReadyQueue.push(onReady);

  if (!window.__idtYouTubeReadyHooked) {
    const previousReadyHandler = window.onYouTubeIframeAPIReady;

    window.onYouTubeIframeAPIReady = function () {
      if (typeof previousReadyHandler === 'function') {
        previousReadyHandler();
      }
      flushYouTubeReadyQueue();
    };

    window.__idtYouTubeReadyHooked = true;
  }

  // Fallback: if the API script was already loaded before the callback hook,
  // poll briefly and initialize as soon as YT.Player becomes available.
  if (!window.__idtYouTubeReadyPolling) {
    window.__idtYouTubeReadyPolling = true;
    let attempts = 0;
    const maxAttempts = 50;
    const intervalId = window.setInterval(() => {
      attempts += 1;
      if (isYouTubePlayerReady() || attempts >= maxAttempts) {
        window.clearInterval(intervalId);
        window.__idtYouTubeReadyPolling = false;

        if (isYouTubePlayerReady()) {
          flushYouTubeReadyQueue();
        }
      }
    }, 100);
  }
}

export function onYouTubeIframeAPIReady(videoContainers) {
  window.videoPlayerOZ = [];
  window.videoPlayerOZReady = false;
  window.resizedVideoIframe = false;

  waitForYouTubePlayer(() => {
    for (let index = 0; index < videoContainers.length; index++) {
      const videoContainer = videoContainers[index];
      const videoPlaceholder = videoContainer.getElementsByClassName('video')[0];
      const videoCover = videoContainer.getElementsByClassName('video-cover')[0];
      const videoUrl = videoContainer.getAttribute('data-video-url');
      const videoID = YouTubeTPlayerMethods.getVideoID(videoUrl);

      if (!videoID || !videoPlaceholder) continue;

      window.videoPlayerOZ[index] = new window.YT.Player(videoPlaceholder, {
        width: '100%',
        height: 340,
        videoId: videoID,
        playerVars: {
          rel: 0,
          modestbranding: 1,
          showinfo: 0,
          disablekb: 1,
          fs: 0,
          autohide: 1
        },
        events: {
          onReady: YouTubeTPlayerMethods.onPlayerReady.bind(this, videoContainers),
          onStateChange: YouTubeTPlayerMethods.onPlayerStateChange.bind(this, videoContainer, videoContainers),
          onError: YouTubeTPlayerMethods.onPlayerError
        }
      });

      if (videoCover) {
        videoCover.addEventListener('click', () => {
          const player = window.videoPlayerOZ[index];
          if (!player) return;

          if (videoContainer.classList.contains('stop')) {
            player.playVideo();
            videoContainer.classList.remove('stop');
          } else {
            player.stopVideo();
            videoContainer.classList.add('stop');
          }
        });

        YouTubeTPlayerMethods.setVideoCoverImage(videoCover, videoID);
      }
    }
  });
}

export const YouTubeTPlayerMethods = {
  videoCoverSrc: 'https://img.youtube.com/vi/',
  videoCoverImageIndex: '0.jpg',
  currentWindowWidth: window.innerWidth,
  onPlayerReady: (videoContainers, e) => {
    window.videoPlayerOZReady = true;

    if (!window.resizedVideoIframe) {
      YouTubeTPlayerMethods.resizeIframe(videoContainers);
    }
    window.resizedVideoIframe = true;
  },

  onPlayerStateChange: (videoContainer, videoContainers, e) => {
    const videoState = e.data;

    // playing
    if (videoState === 1) {
      videoContainer.classList.remove('stop');
      videoContainer.classList.add('playing');

      YouTubeTPlayerMethods.stopPlayingVideos(videoContainers, videoContainer);
    }

    // unstarted, paused, ended
    if (videoState === -1 || videoState === 2 || videoState === 0) {
      videoContainer.classList.remove('playing');
      videoContainer.classList.add('stop');
    }

    if (window.innerWidth !== YouTubeTPlayerMethods.currentWindowWidth) {
      YouTubeTPlayerMethods.currentWindowWidth = window.innerWidth;
      YouTubeTPlayerMethods.resizeIframe(videoContainers);
    }
  },

  onPlayerError: (e) => {
    // eslint-disable-next-line no-console
    console.error('ERRORE', e);
  },

  getVideoID: (videoUrl) => {
    // eslint-disable-next-line no-useless-escape
    const regExp = /.*(?:youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=)([^#\&\?]*).*/;
    const match = videoUrl.match(regExp);
    return match && match[1].length === 11 ? match[1] : null;
  },

  stopPlayingVideos: (videoContainers, currentPlayingVideo) => {
    const videoContainersTotal = videoContainers.length;
    if (!videoContainersTotal) return;

    for (let index = 0; index < videoContainersTotal; index++) {
      const videoContainer = videoContainers[index];
      const player = window.videoPlayerOZ[index];
      if (player && currentPlayingVideo.id !== videoContainer.id && videoContainer.classList.contains('playing')) {
        player.stopVideo();
        videoContainer.classList.remove('playing');
        videoContainer.classList.add('stop');
      }
    }
  },

  setIframeSize: (videoCover, iframe) => {
    const size = YouTubeTPlayerMethods.getIframeSize(videoCover, iframe);

    iframe.style.width = `${size.w}px`;
    iframe.style.height = `${size.h}px`;
  },

  getIframeSize: (videoCover) => {
    const videoCoverRect = videoCover.getBoundingClientRect();
    // video size: 16:9
    const h = videoCoverRect.width / 1.8;
    const w = videoCoverRect.width;

    return { h, w };
  },

  setVideoCoverImage: (cover, videoID) => {
    const imgElement = document.createElement('img');
    imgElement.src = `${YouTubeTPlayerMethods.videoCoverSrc}${videoID}/${YouTubeTPlayerMethods.videoCoverImageIndex}`;

    cover.append(imgElement);
  },

  resizeIframe: (videoContainers) => {
    if (!videoContainers) return;

    const slideTotal = videoContainers.length;

    for (let index = 0; index < slideTotal; index++) {
      const slide = videoContainers[index];
      const iframe = slide.getElementsByTagName('iframe')[0];
      YouTubeTPlayerMethods.setIframeSize(slide, iframe);
    }
  }
};
